/**
 * Intro Clip Configuration Component
 * 
 * Allows users to configure intro clip settings with design customization.
 */

import React from 'react';
import { IntroClipConfig, defaultOverlayConfig } from '../../types/overlayConfig';

interface IntroConfigProps {
  introConfig: IntroClipConfig;
  onIntroChange: (config: IntroClipConfig) => void;
}

const IntroConfig: React.FC<IntroConfigProps> = React.memo(({
  introConfig,
  onIntroChange,
}) => {
  const defaultIntro = defaultOverlayConfig.introOutro?.intro!;
  
  // Temporarily disable gradient settings (visible but non-interactable)
  const isGradientDisabled = true;

  const gradientTypeOptions: Array<{ value: IntroClipConfig['gradient']['type']; label: string }> = [
    { value: 'horizontal', label: 'Horizontal' },
    { value: 'vertical', label: 'Vertical' },
    { value: 'radial', label: 'Radial' },
  ];

  const presetOptions: Array<{ value: IntroClipConfig['preset']; label: string }> = [
    { value: 'preset1', label: 'Preset 1 (Simple)' },
    { value: 'preset2', label: 'Preset 2 (Detailed)' },
  ];

  // Shared gradient configuration component
  const GradientConfig: React.FC<{
    gradient: IntroClipConfig['gradient'];
    backgroundColor: string;
    textColor: string;
    onChange: (gradient: IntroClipConfig['gradient']) => void;
    isDisabled?: boolean;
  }> = ({ gradient, backgroundColor, textColor, onChange, isDisabled = false }) => (
    <div className={`space-y-2 ${isGradientDisabled ? 'opacity-60' : ''}`}>
      <div className="flex items-center space-x-2">
        <input
          type="checkbox"
          id="enable-gradient-intro"
          checked={gradient?.enabled ?? false}
          disabled={isGradientDisabled}
          onChange={(e) => !isGradientDisabled && onChange({
            ...gradient,
            enabled: e.target.checked,
            type: gradient?.type || 'horizontal',
            direction: gradient?.direction ?? 0,
            startColor: gradient?.startColor || backgroundColor,
            endColor: gradient?.endColor || textColor,
            stops: gradient?.stops || [50, 50],
          })}
          className={`w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 ${isDisabled ? 'opacity-50 cursor-not-allowed' : ''}`}
        />
        <label htmlFor="enable-gradient-intro" className={`text-xs font-medium ${isDisabled ? 'text-gray-400 cursor-not-allowed' : 'text-gray-700'}`}>
          Enable Gradient
        </label>
      </div>

      {gradient?.enabled && (
        <div className="space-y-2 pl-6">
          {/* Gradient Type */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1.5">
              Gradient Type
            </label>
            <select
              value={gradient.type || 'horizontal'}
              onChange={(e) => onChange({
                ...gradient!,
                type: e.target.value as 'horizontal' | 'vertical' | 'radial',
              })}
              className="w-full px-2 py-1.5 border border-gray-300 rounded-md text-xs focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              {gradientTypeOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          {/* Direction (for horizontal/vertical) */}
          {gradient.type !== 'radial' && (
            <div>
              <label className={`block text-xs font-medium mb-1.5 ${isDisabled ? 'text-gray-400' : 'text-gray-700'}`}>
                Direction: {gradient.direction ?? 0}°
              </label>
              <input
                type="range"
                min="0"
                max="360"
                step="1"
                value={gradient.direction ?? 0}
                disabled={isGradientDisabled}
                onChange={(e) => !isGradientDisabled && onChange({
                  ...gradient!,
                  direction: parseInt(e.target.value),
                })}
                className={`w-full h-2 bg-gray-200 rounded-lg appearance-none accent-blue-600 ${isGradientDisabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
              />
            </div>
          )}

          {/* Radial Center Position */}
          {gradient.type === 'radial' && (
            <div className="space-y-2">
              <div>
                <label className={`block text-xs font-medium mb-1.5 ${isDisabled ? 'text-gray-400' : 'text-gray-700'}`}>
                  Center X (%)
                </label>
                <div className="flex items-center space-x-2">
                  <input
                    type="range"
                    min="0"
                    max="100"
                    step="1"
                    value={gradient.stops?.[0] ?? 50}
                    disabled={isDisabled}
                    onChange={(e) => !isDisabled && onChange({
                      ...gradient!,
                      stops: [parseInt(e.target.value), gradient.stops?.[1] ?? 50],
                    })}
                    className={`flex-1 h-2 bg-gray-200 rounded-lg appearance-none accent-blue-600 ${isDisabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                  />
                  <span className="text-xs text-gray-600 w-10 text-right">
                    {gradient.stops?.[0] ?? 50}%
                  </span>
                </div>
              </div>
              <div>
                <label className={`block text-xs font-medium mb-1.5 ${isDisabled ? 'text-gray-400' : 'text-gray-700'}`}>
                  Center Y (%)
                </label>
                <div className="flex items-center space-x-2">
                  <input
                    type="range"
                    min="0"
                    max="100"
                    step="1"
                    value={gradient.stops?.[1] ?? 50}
                    disabled={isDisabled}
                    onChange={(e) => !isDisabled && onChange({
                      ...gradient!,
                      stops: [gradient.stops?.[0] ?? 50, parseInt(e.target.value)],
                    })}
                    className={`flex-1 h-2 bg-gray-200 rounded-lg appearance-none accent-blue-600 ${isDisabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                  />
                  <span className="text-xs text-gray-600 w-10 text-right">
                    {gradient.stops?.[1] ?? 50}%
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Start Color */}
          <div>
            <label className={`block text-xs font-medium mb-1.5 ${isDisabled ? 'text-gray-400' : 'text-gray-700'}`}>
              Start Color
            </label>
            <div className="flex items-center space-x-2">
              <input
                type="color"
                value={gradient.startColor || backgroundColor}
                disabled={isDisabled}
                onChange={(e) => !isDisabled && onChange({
                  ...gradient!,
                  startColor: e.target.value,
                })}
                className={`w-8 h-8 border border-gray-300 rounded ${isDisabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
              />
              <input
                type="text"
                value={gradient.startColor || backgroundColor}
                disabled={isDisabled}
                onChange={(e) => !isDisabled && onChange({
                  ...gradient!,
                  startColor: e.target.value,
                })}
                className={`flex-1 px-2 py-1 border border-gray-300 rounded-md text-xs focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${isDisabled ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`}
                placeholder="#000000"
              />
            </div>
          </div>

          {/* End Color */}
          <div>
            <label className={`block text-xs font-medium mb-1.5 ${isDisabled ? 'text-gray-400' : 'text-gray-700'}`}>
              End Color
            </label>
            <div className="flex items-center space-x-2">
              <input
                type="color"
                value={gradient.endColor || textColor}
                disabled={isDisabled}
                onChange={(e) => !isDisabled && onChange({
                  ...gradient!,
                  endColor: e.target.value,
                })}
                className={`w-8 h-8 border border-gray-300 rounded ${isDisabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
              />
              <input
                type="text"
                value={gradient.endColor || textColor}
                disabled={isDisabled}
                onChange={(e) => !isDisabled && onChange({
                  ...gradient!,
                  endColor: e.target.value,
                })}
                className={`flex-1 px-2 py-1 border border-gray-300 rounded-md text-xs focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${isDisabled ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`}
                placeholder="#FFFFFF"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );

  // Intro handlers
  const handleIntroReset = () => {
    onIntroChange({
      ...introConfig,
      textColor: defaultIntro.textColor,
      backgroundColor: defaultIntro.backgroundColor,
      opacity: defaultIntro.opacity,
      gradient: {
        enabled: defaultIntro.gradient.enabled,
        type: defaultIntro.gradient.type,
        direction: defaultIntro.gradient.direction,
        startColor: defaultIntro.gradient.startColor,
        endColor: defaultIntro.gradient.endColor,
        stops: defaultIntro.gradient.stops,
      },
    });
  };

  return (
    <div className="space-y-3">
      {/* Duration */}
      <div>
        <label className="block text-xs font-medium text-gray-700 mb-1.5">
          Duration: {introConfig.duration}s
        </label>
        <input
          type="range"
          min="3"
          max="7"
          step="0.5"
          value={introConfig.duration}
          onChange={(e) => onIntroChange({
            ...introConfig,
            duration: parseFloat(e.target.value),
          })}
          className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
        />
        <div className="flex justify-between text-xs text-gray-500 mt-1">
          <span>3s</span>
          <span>7s</span>
        </div>
      </div>

      {/* Preset Selection */}
      <div>
        <label className="block text-xs font-medium text-gray-700 mb-1.5">
          Design Preset
        </label>
        <select
          value={introConfig.preset}
          onChange={(e) => onIntroChange({
            ...introConfig,
            preset: e.target.value as 'preset1' | 'preset2',
          })}
          className="w-full px-2 py-1.5 border border-gray-300 rounded-md text-xs focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        >
          {presetOptions.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>

      {/* Design Settings - Colors */}
      <div className="space-y-2 pt-2 border-t border-gray-200">
        <div className="flex items-center justify-between">
          <label className="text-xs font-medium text-gray-700">Colors</label>
          <button
            onClick={handleIntroReset}
            className="text-xs text-blue-600 hover:text-blue-800"
          >
            Reset
          </button>
        </div>
        
        <div className="space-y-2">
          {/* Text Color */}
          <div>
            <label className="block text-xs text-gray-600 mb-1">
              Text Color
              <span className="ml-1 text-gray-400 cursor-help" title="Color of all text overlays (titles, messages, etc.)">(?)</span>
            </label>
            <div className="flex items-center space-x-2">
              <input
                type="color"
                value={introConfig.textColor}
                onChange={(e) => onIntroChange({
                  ...introConfig,
                  textColor: e.target.value,
                  gradient: introConfig.gradient ? {
                    ...introConfig.gradient,
                    endColor: introConfig.gradient.endColor === introConfig.textColor 
                      ? e.target.value 
                      : introConfig.gradient.endColor,
                  } : introConfig.gradient,
                })}
                className="w-8 h-8 border border-gray-300 rounded cursor-pointer"
              />
              <input
                type="text"
                value={introConfig.textColor}
                onChange={(e) => onIntroChange({
                  ...introConfig,
                  textColor: e.target.value,
                  gradient: introConfig.gradient ? {
                    ...introConfig.gradient,
                    endColor: introConfig.gradient.endColor === introConfig.textColor 
                      ? e.target.value 
                      : introConfig.gradient.endColor,
                  } : introConfig.gradient,
                })}
                className="flex-1 px-2 py-1 border border-gray-300 rounded-md text-xs focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="#FFFFFF"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Opacity */}
      <div className="space-y-2 pt-2 border-t border-gray-200">
        <label className="block text-xs font-medium text-gray-700">Opacity</label>
        <div className="flex items-center space-x-2">
          <input
            type="range"
            min="0"
            max="1"
            step="0.1"
            value={introConfig.opacity ?? 1.0}
            onChange={(e) => onIntroChange({
              ...introConfig,
              opacity: parseFloat(e.target.value),
            })}
            className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
          />
          <span className="text-xs text-gray-600 w-10 text-right">
            {((introConfig.opacity ?? 1.0) * 100).toFixed(0)}%
          </span>
        </div>
      </div>

      {/* Gradient */}
      <div className="space-y-2 pt-2 border-t border-gray-200">
        <GradientConfig
          gradient={introConfig.gradient}
          backgroundColor={introConfig.backgroundColor}
          textColor={introConfig.textColor}
          isDisabled={isGradientDisabled}
          onChange={(gradient) => onIntroChange({
            ...introConfig,
            gradient,
          })}
        />
      </div>

      {/* Background Color */}
      <div className="space-y-2 pt-2 border-t border-gray-200">
        <label className="block text-xs font-medium text-gray-700 mb-1.5">
          Background Color
          <span className="ml-1 text-gray-400 font-normal cursor-help" title="Base background color of the clip (used when gradient is disabled)">(?)</span>
        </label>
        <div className="flex items-center space-x-2">
          <input
            type="color"
            value={introConfig.backgroundColor}
            onChange={(e) => onIntroChange({
              ...introConfig,
              backgroundColor: e.target.value,
              gradient: introConfig.gradient ? {
                ...introConfig.gradient,
                startColor: introConfig.gradient.startColor === introConfig.backgroundColor 
                  ? e.target.value 
                  : introConfig.gradient.startColor,
              } : introConfig.gradient,
            })}
            className="w-8 h-8 border border-gray-300 rounded cursor-pointer"
          />
          <input
            type="text"
            value={introConfig.backgroundColor}
            onChange={(e) => onIntroChange({
              ...introConfig,
              backgroundColor: e.target.value,
              gradient: introConfig.gradient ? {
                ...introConfig.gradient,
                startColor: introConfig.gradient.startColor === introConfig.backgroundColor 
                  ? e.target.value 
                  : introConfig.gradient.startColor,
              } : introConfig.gradient,
            })}
            className="flex-1 px-2 py-1 border border-gray-300 rounded-md text-xs focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="#000000"
          />
        </div>
      </div>

      {/* Content-specific settings (for 'preset2' - Detailed) */}
      {introConfig.preset === 'preset2' && (
        <div className="space-y-2 pt-2 border-t border-gray-200">
          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              id="show-tournament-logo-intro"
              checked={introConfig.showTournamentLogo ?? true}
              onChange={(e) => onIntroChange({
                ...introConfig,
                showTournamentLogo: e.target.checked,
              })}
              className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            />
            <label htmlFor="show-tournament-logo-intro" className="text-xs text-gray-700">
              Show tournament logo
            </label>
          </div>

          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              id="show-team-logos-intro"
              checked={introConfig.showTeamLogos ?? true}
              onChange={(e) => onIntroChange({
                ...introConfig,
                showTeamLogos: e.target.checked,
              })}
              className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            />
            <label htmlFor="show-team-logos-intro" className="text-xs text-gray-700">
              Show team logos
            </label>
          </div>

          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              id="show-lineups-intro"
              checked={introConfig.showLineups ?? true}
              onChange={(e) => onIntroChange({
                ...introConfig,
                showLineups: e.target.checked,
              })}
              className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            />
            <label htmlFor="show-lineups-intro" className="text-xs text-gray-700">
              Show lineups (if available)
            </label>
          </div>
        </div>
      )}
    </div>
  );
});

IntroConfig.displayName = 'IntroConfig';

export default IntroConfig;
